import { QrCode, type Rec, useCompany, useSignatureFile } from '@tnn/app-kit'
import { formatArea, formatDateTime, formatMoney, formatNumber, formatPhone, labels, type RefMap } from '@tnn/core'
import {
  type ServiceLine,
  type Signature,
  serviceAmount,
  type TermsVersion,
  termsPoints,
  type WaybillItem,
  waybill,
  waybillTotalsOf,
} from '@tnn/domain'
import JsBarcode from 'jsbarcode'
import { Fragment, useLayoutEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { termsLink, trackLink, waybillLink } from './scan'
import { servicePrice } from './services'
import { TermsPoint } from './TermsPoint'
import classes from './WaybillDocument.module.css'

type Party = 'sender' | 'receiver'

/** A bar's narrowest width on paper: wide enough for an ordinary hand scanner, a phone's camera, a 300 dpi printer */
const MODULE_MM = 0.25

/**
 * The bill's number as a Code 128 barcode, under the number in words: a hand scanner (or the camera) types it into the
 * loading screen's box. Bars only, as wide as the number needs at `MODULE_MM` a bar
 */
function Barcode({ value }: { value: string }) {
  const svg = useRef<SVGSVGElement>(null)
  useLayoutEffect(() => {
    const el = svg.current
    if (!el || !value) return
    JsBarcode(el, value, { format: 'CODE128', displayValue: false, margin: 0, width: 1, height: 24, background: 'transparent' })
    // Drawn one unit a bar: its width in units is its bars, laid out at a size that stays readable when printed
    const modules = Number(el.getAttribute('width')?.replace('px', '')) || 0
    el.setAttribute('viewBox', `0 0 ${modules} 24`)
    el.setAttribute('preserveAspectRatio', 'none')
    el.style.width = `${modules * MODULE_MM}mm`
  }, [value])
  return <svg ref={svg} className={classes.barcode} role="img" aria-label={value} />
}

/** Lines the goods table always has: a bill's goods and add-ons, the rest left blank, so every copy keeps one shape */
const TABLE_LINES = 10
type Copy = 'original' | 'copy'

/** A4 with two copies a sheet, cut apart along the dashes, or A5 on its side with one: the copies are the same size */
export type WaybillPaper = 'a4' | 'a5'

/** What one print of a bill puts out: the original or not, and how many copies */
export interface WaybillSheets {
  original: boolean
  copies: number
}

/** The counter's print: the original and three copies */
export const usualSheets: WaybillSheets = { original: true, copies: 3 }

/**
 * The waybill: two copies on an A4 sheet, one on an A5. The original is the sender's; the copies go to the
 * receiver, with the goods and to the files. Every QR opens the bill's tracking page (no account), which the
 * app's scanner reads as the bill too. Every sheet carries the destination branch and the piece count, large
 * enough for a sorter to read from a distance, and the key points of the shipping
 * terms the bill was booked under (`terms`), which the sender signs as agreed to.
 */
export function WaybillDocument({
  record,
  refs,
  terms,
  senderSignature,
  paper = 'a4',
  sheets = usualSheets,
}: {
  record: Rec
  refs: RefMap
  terms?: TermsVersion
  /** Signed on the counter's screen: printed on the sender's line */
  senderSignature?: Signature | null
  paper?: WaybillPaper
  sheets?: WaybillSheets
}) {
  const copies: Copy[] = [
    ...(sheets.original ? (['original'] as const) : []),
    ...Array.from({ length: sheets.copies }, () => 'copy' as const),
  ]
  const perSheet = paper === 'a4' ? 2 : 1
  const pages = Array.from({ length: Math.ceil(copies.length / perSheet) }, (_, i) => copies.slice(i * perSheet, (i + 1) * perSheet))
  return pages.map((page, i) => (
    // biome-ignore lint/suspicious/noArrayIndexKey: the copies are alike
    <div key={i} className={classes.page} data-paper={paper}>
      {page.map((copy, j) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: the copies are alike
        <Fragment key={j}>
          {j > 0 && <div className={classes.cut} />}
          <WaybillCopy record={record} refs={refs} copy={copy} terms={terms} senderSignature={senderSignature} />
        </Fragment>
      ))}
    </div>
  ))
}

function WaybillCopy({
  record,
  refs,
  copy,
  terms,
  senderSignature,
}: {
  record: Rec
  refs: RefMap
  copy: Copy
  terms?: TermsVersion
  senderSignature?: Signature | null
}) {
  const { t } = useTranslation()
  const company = useCompany().data
  const signatureFile = useSignatureFile()
  const f = (name: string) => t(`tms:waybill.fields.${name}`)
  const k = (key: string, values?: Record<string, unknown>) => t(`tms:waybill.print.${key}`, values)
  const branch = (id: unknown) => (id ? refs['company.branch']?.[String(id)] : undefined)
  const items = (record.items ?? []) as WaybillItem[]
  const services = (record.services ?? []) as ServiceLine[]
  // The branches between its two ends: the origin is in the strip, the destination in its cell
  const via = ((record.route ?? []) as string[]).slice(1, -1).map(branch).filter(Boolean)
  const option = (field: string) => t(labels.option(waybill, field, String(record[field])))
  // Every copy's QR opens the bill's tracking page; the app's scanner reads it as the bill too (billCodeFromScan)
  const tracked = record.trackingCode ? String(record.trackingCode) : undefined

  const totals = waybillTotalsOf(record)
  const paid = record.paymentStatus === 'paid'

  const party = (p: Party) => (
    <div className={classes.party}>
      <div className={classes.name}>
        <span className={classes.label}>{t(`tms:waybill.sections.${p}`)}</span> {String(record[`${p}Name`] ?? '')}{' '}
        <span className={classes.phone}>{formatPhone(record[`${p}Phone`] as string)}</span>
      </div>
      <div className={classes.address}>
        {[
          record[`${p}Address`],
          formatArea({
            subdistrict: record[`${p}Subdistrict`] as string,
            district: record[`${p}District`] as string,
            province: record[`${p}Province`] as string,
            postcode: record[`${p}Postcode`] as string,
          }),
        ]
          .filter(Boolean)
          .join(' ')}
      </div>
    </div>
  )

  return (
    <article className={classes.copy}>
      {/*
        Like a parcel label: a strip saying who issued what, then one row of cells to read from across a room — the
        bill's number, where it goes, how many pieces, the QR
      */}
      <header className={classes.head}>
        <div className={classes.strip}>
          <div className={classes.issuer}>
            <span className={classes.companyName}>{company?.name}</span>
            {' · '}
            {branch(record.originBranchId)}
            {company?.phone ? ` · ${formatPhone(company.phone)}` : ''}
          </div>
          <div className={classes.docTitle}>
            {t('tms:waybill.title')} <span className={classes.copyTag}>{k(copy)}</span>{' '}
            <span className={classes.muted}>{formatDateTime(String(record.bookedAt ?? ''), { style: 'short' })}</span>
          </div>
        </div>
        <div className={classes.cells}>
          <div className={classes.cell}>
            <div className={classes.number}>{String(record.number ?? '')}</div>
            <Barcode value={String(record.number ?? '')} />
          </div>
          <div className={`${classes.cell} ${classes.destCell}`}>
            <div className={classes.destName}>
              <span className={classes.label}>{f('destBranchId')}</span> {branch(record.destBranchId) ?? '–'}
            </div>
            <div className={classes.way}>
              {via.length ? `${k('via', { stops: via.join(' → ') })} · ` : ''}
              {option('destMode')}
            </div>
          </div>
          <div className={`${classes.cell} ${classes.piecesCell}`}>
            <div className={classes.pieces}>
              {formatNumber(Number(record.pieces ?? 0))} <span>{t('tms:waybill.print.pieces')}</span>
            </div>
          </div>
          <QrCode
            paper
            className={`${classes.cell} ${classes.qr}`}
            value={tracked ? trackLink(tracked) : waybillLink(String(record.id))}
            size={44}
            label={k(tracked ? 'track' : 'scan')}
          />
        </div>
      </header>

      <section className={classes.parties}>
        {party('sender')}
        {party('receiver')}
      </section>

      {/* The goods, a line each: numbered, figures right-aligned in their own columns; add-on services after them */}
      <table className={classes.items}>
        <colgroup>
          <col className={classes.colNo} />
          <col />
          <col className={classes.colQty} />
          <col className={classes.colKg} />
          <col className={classes.colMoney} />
          <col className={classes.colMoney} />
        </colgroup>
        <thead>
          <tr>
            <th data-num>#</th>
            <th>{t('tms:waybill.item.name')}</th>
            <th data-num>{t('tms:waybill.item.qty')}</th>
            <th data-num>{t('tms:waybill.item.weightKg')}</th>
            <th data-num>{t('tms:waybill.item.rate')}</th>
            <th data-num>{t('tms:waybill.item.amount')}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: lines have no id
            <tr key={i}>
              <td data-num className={classes.no}>
                {i + 1}
              </td>
              <td className={classes.goods}>{item.name}</td>
              {/* A charge line (a return bill's freight) is money only */}
              <td data-num>
                {item.charge ? (
                  ''
                ) : (
                  <>
                    {formatNumber(item.qty)} <span className={classes.unit}>{item.unit ?? ''}</span>
                  </>
                )}
              </td>
              <td data-num>{item.weightKg != null && !item.charge ? formatNumber(item.weightKg) : '–'}</td>
              <td data-num>{formatMoney(item.rate)}</td>
              <td data-num>{formatMoney(item.qty * item.rate)}</td>
            </tr>
          ))}
          {/* A service by the piece or weight has the pieces or kg it is for in their columns */}
          {services.map((line) => (
            <tr key={line.serviceId ?? line.name} className={classes.serviceRow}>
              <td />
              <td className={classes.goods}>
                <span className={classes.tag}>{k('service')}</span> {line.name}{' '}
                <span className={classes.unit}>{servicePrice(line, t)}</span>
              </td>
              <td data-num>
                {line.basis === 'piece' && line.quantity != null && (
                  <>
                    {formatNumber(line.quantity)} <span className={classes.unit}>{t('tms:service.line.units.piece')}</span>
                  </>
                )}
              </td>
              <td data-num>{line.basis === 'weight' && line.quantity != null ? formatNumber(line.quantity) : ''}</td>
              <td />
              <td data-num>{formatMoney(serviceAmount(line))}</td>
            </tr>
          ))}
          {Array.from({ length: Math.max(TABLE_LINES - items.length - services.length, 0) }, (_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: blank lines are alike
            <tr key={`blank-${i}`} aria-hidden>
              <td />
              <td />
              <td />
              <td />
              <td />
              <td />
            </tr>
          ))}
        </tbody>
      </table>

      {/* Who pays and what it comes to: the money read in one place, stamped once it is paid */}
      <section className={classes.summary}>
        <div className={classes.payment}>
          <div>
            {k('goods', { pieces: formatNumber(totals.pieces), kg: formatNumber(totals.weightKg) })}
            {record.declaredValue ? ` · ${k('declared')} ${formatMoney(Number(record.declaredValue))}` : ''}
            {' · '}
            {f('payer')}: <strong>{option('payer')}</strong>
            {paid ? null : <span className={classes.unit}> · {option('paymentStatus')}</span>}
          </div>
          {record.reference || record.notes ? (
            <div className={classes.unit}>
              {[record.reference ? `${f('reference')} ${String(record.reference)}` : '', String(record.notes ?? '')]
                .filter(Boolean)
                .join(' · ')}
            </div>
          ) : null}
        </div>
        {/* Stamps, as a counter would ink them: paid, and that the receiver collects it at the branch */}
        {paid || record.destMode === 'branch' ? (
          <div className={classes.stamps}>
            {record.destMode === 'branch' && <div className={classes.stamp}>{option('destMode')}</div>}
            {paid && <div className={classes.stamp}>{k('paidStamp')}</div>}
          </div>
        ) : null}
        {/* The freight and the add-ons small and stacked, beside the total they make */}
        <div className={classes.totals}>
          {totals.servicesFee > 0 && (
            <dl className={classes.parts}>
              <dt>{t('tms:service.line.freight')}</dt>
              <dd>{formatMoney(totals.freight)}</dd>
              <dt>{t('tms:service.line.services')}</dt>
              <dd>{formatMoney(totals.servicesFee)}</dd>
            </dl>
          )}
          <div className={classes.grand}>
            {t('tms:service.line.total')} <span>{formatMoney(Number(record.fee ?? 0))}</span>
          </div>
        </div>
      </section>

      {/* The terms' key points on the left, beside what the sender signs to and the lines they sign on */}
      <footer className={classes.foot}>
        {terms?.number ? (
          <section className={classes.terms}>
            <div className={classes.termsHead}>{t('tms:terms.print.heading', { number: String(terms.number) })}</div>
            <ol>
              {termsPoints(terms.summary).map((point) => (
                <li key={point}>
                  <TermsPoint point={point} />
                </li>
              ))}
            </ol>
          </section>
        ) : (
          <div />
        )}
        <div className={classes.signing}>
          {terms?.number ? (
            <div className={classes.agree}>
              {t('tms:terms.print.agree', {
                number: String(terms.number),
                link: termsLink(String(terms.number)).replace(/^https?:\/\//, ''),
              })}
            </div>
          ) : null}
          <div className={classes.signs}>
            {['senderSign', 'staffSign'].map((key) => {
              // The sender signed (here, on a link): their signature on the line, and when; signed on paper, that it was
              const signed = key === 'senderSign' ? senderSignature : null
              return (
                <div key={key} className={classes.sign}>
                  <div className={classes.line}>
                    {signed &&
                      (signed.method === 'paper' ? (
                        <span className={classes.unit}>{t('signing.onPaper')}</span>
                      ) : (
                        <img src={signatureFile(signed.fileId)} alt={signed.name} />
                      ))}
                  </div>
                  {k(key)}
                  {signed && <div className={classes.unit}>{formatDateTime(signed.signedAt, { style: 'short' })}</div>}
                </div>
              )
            })}
          </div>
        </div>
      </footer>
    </article>
  )
}
